import { Check, ChevronDown } from 'lucide-react'
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/utils/cn'

import { baseControl } from './styles'

export interface MultiSelectOption {
  value: string
  label: string
}

interface MultiSelectProps {
  id?: string
  value: string[]
  onChange: (value: string[]) => void
  options: MultiSelectOption[]
  /** Texto com nada marcado (ex.: "Todas"). */
  placeholder?: string
  invalid?: boolean
  disabled?: boolean
  /** Lista ainda carregando (ex.: vinda da API). */
  loading?: boolean
  /** Mostra "Marcar todas" no topo da lista (marca todas as opções visíveis). */
  marcarTodas?: boolean
  className?: string
}

interface Position {
  top: number
  left: number
  width: number
  maxHeight: number
}

const GAP = 4
const VIEWPORT_MARGIN = 8
const MAX_HEIGHT = 256

/**
 * Select de múltipla escolha: o campo mostra os itens marcados e abre uma lista com caixas
 * de marcar. A lista é portalizada no `<body>` (como o `Menu`) para não ser cortada pelo
 * `overflow` do Modal, e fica acima dele (z-60, como o calendário do DateTimePicker).
 */
export function MultiSelect({
  id,
  value,
  onChange,
  options,
  placeholder = 'Selecione',
  invalid,
  disabled,
  loading,
  marcarTodas,
  className,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<Position | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  const selecionados = options.filter((option) => value.includes(option.value))
  const resumo = selecionados.map((option) => option.label).join(', ')

  // Abaixo do gatilho; se não couber e houver mais espaço acima, abre para cima.
  useLayoutEffect(() => {
    if (!open) return
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    const espacoAbaixo = window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN
    const espacoAcima = rect.top - GAP - VIEWPORT_MARGIN
    const paraCima = espacoAbaixo < MAX_HEIGHT && espacoAcima > espacoAbaixo
    const maxHeight = Math.min(MAX_HEIGHT, paraCima ? espacoAcima : espacoAbaixo)
    setPosition({
      top: paraCima ? rect.top - GAP - maxHeight : rect.bottom + GAP,
      left: rect.left,
      width: rect.width,
      maxHeight,
    })
  }, [open])

  useEffect(() => {
    if (!open) return
    const fechar = () => setOpen(false)
    const dentro = (target: EventTarget | null) =>
      triggerRef.current?.contains(target as Node) || listRef.current?.contains(target as Node)

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!dentro(event.target)) fechar()
    }
    // Rolar a própria lista não fecha; rolar a página/modal sim (a posição ficaria errada).
    const onScroll = (event: Event) => {
      if (!listRef.current?.contains(event.target as Node)) fechar()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Aberto dentro de um Modal: o Esc fecha só a lista.
      event.stopImmediatePropagation()
      fechar()
      triggerRef.current?.focus()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKey, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', fechar)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', fechar)
    }
  }, [open])

  const alternar = (opcao: string) =>
    onChange(value.includes(opcao) ? value.filter((atual) => atual !== opcao) : [...value, opcao])

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-invalid={invalid || undefined}
        title={resumo || undefined}
        onClick={() => setOpen((aberto) => !aberto)}
        className={cn(
          baseControl,
          'flex items-center gap-2 text-left',
          invalid ? 'border-critical' : 'border-line',
          className,
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !resumo && 'text-fg-subtle')}>
          {resumo || placeholder}
        </span>
        {selecionados.length > 1 && (
          <span className="shrink-0 rounded-full bg-brand/10 px-1.5 text-xs font-medium text-brand">
            {selecionados.length}
          </span>
        )}
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-fg-subtle transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-multiselectable="true"
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
            }}
            className="pc-animate-modal fixed z-[60] overflow-y-auto rounded-xl border border-line bg-surface py-1 shadow-lg"
          >
            {(value.length > 0 || (marcarTodas && options.length > 0)) && (
              <div className="flex items-center gap-3 px-3 py-1.5 text-xs font-medium text-brand">
                {marcarTodas && selecionados.length < options.length && (
                  <button
                    type="button"
                    onClick={() => onChange(options.map((option) => option.value))}
                    className="hover:underline"
                  >
                    Marcar todas
                  </button>
                )}
                {value.length > 0 && (
                  <button type="button" onClick={() => onChange([])} className="hover:underline">
                    Limpar seleção
                  </button>
                )}
              </div>
            )}
            {loading && <p className="px-3 py-2 text-sm text-fg-subtle">Carregando…</p>}
            {!loading && options.length === 0 && (
              <p className="px-3 py-2 text-sm text-fg-subtle">Nenhuma opção.</p>
            )}
            {options.map((option) => {
              const marcado = value.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={marcado}
                  onClick={() => alternar(option.value)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-fg hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none"
                >
                  <span
                    className={cn(
                      'grid size-4 shrink-0 place-items-center rounded border',
                      marcado ? 'border-brand bg-brand text-white' : 'border-line',
                    )}
                  >
                    {marcado && <Check className="size-3" />}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                </button>
              )
            })}
          </div>,
          document.body,
        )}
    </>
  )
}
